/**
 * Le Vanilla + Map shell — the map and active camera share one full-viewport
 * canvas. Event details, cameras, and navigation float above that canvas so
 * the live event remains the visual focus at every breakpoint.
 *
 * The shell owns the map↔camera mode state. The shared view dock controls
 * which overlay panel is visible; the canvas itself stays mounted and
 * full-screen.
 *
 * Slots:
 *   - topbar           : Topbar primitive
 *   - player           : MainPlayer — full-canvas camera layer
 *   - map              : RouteMap   — full-canvas map layer
 *   - below_player     : floating event details panel
 *   - sidebar          : floating camera / event context panel
 *   - mobile_switcher  : universal view dock (about / cameras / map)
 */

import { useCallback, useMemo, useState } from 'react'
import {
  registerShell,
  ShellSurfaceProvider,
  type ShellProps,
  type ShellSurfaceMode,
} from '../../layout'
import { VanillaCanvasFrame } from '../le-vanilla/VanillaCanvasFrame'
import './LeVanillaMap.css'

function LeVanillaMapShell({ slots }: ShellProps) {
  // The map and camera stay mounted as persistent layers. The view dock
  // controls which floating panel is visible, independently of this canvas.
  const [surfaceMode, setSurfaceMode] = useState<ShellSurfaceMode>('map')

  const requestMode = useCallback((next: ShellSurfaceMode) => setSurfaceMode(next), [])

  const surfaceContext = useMemo(
    () => ({ mode: surfaceMode, requestMode }),
    [surfaceMode, requestMode],
  )

  return (
    <ShellSurfaceProvider value={surfaceContext}>
      <VanillaCanvasFrame
        className="lv-shell-map"
        slots={slots}
        canvas={(
          <div
            className={`lv-surface-wrap${
              surfaceMode === 'camera' ? ' lv-surface-camera' : ' lv-surface-map'
            }`}
          >
            <div className="lv-surface">
              {/* Both layers remain mounted so switching never rebuilds the map or player. */}
              <div className="lv-surface-map-layer" aria-hidden={surfaceMode !== 'map'} {...(surfaceMode !== 'map' ? { inert: '' } : {})}>{slots.map}</div>
              <div className="lv-surface-camera-layer" aria-hidden={surfaceMode !== 'camera'} {...(surfaceMode !== 'camera' ? { inert: '' } : {})}>{slots.player}</div>

              {slots.player_overlay && (
                <div className="lv-player-overlay">{slots.player_overlay}</div>
              )}

            </div>
          </div>
        )}
      />
    </ShellSurfaceProvider>
  )
}

registerShell('le_vanilla_map', LeVanillaMapShell)

export { LeVanillaMapShell }
